import {
  useBlocker,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { BriefingCreateWorkflowHeader } from "@/components/scheduled-briefings/briefing-create/BriefingCreateWorkflowHeader";
import { ChooseTemplateSectionHeader } from "@/components/scheduled-briefings/briefing-create/ChooseTemplateSectionHeader";
import { ChooseTemplateView } from "@/components/scheduled-briefings/briefing-create/ChooseTemplateView";
import {
  ConfigureBriefingView,
  getSlotsInLayoutOrder,
} from "@/components/scheduled-briefings/briefing-create/ConfigureBriefingView";
import ScheduledBriefingsModals from "@/components/scheduled-briefings/modals/ScheduledBriefingsModals";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/toast";
import type { BriefingDefinitionRepositoryError } from "@/lib/scheduled-briefings/repository/briefingDefinitionRepository.types";
import {
  DEFAULT_BRIEFING_NAME,
  getTemplateBriefingTitle,
} from "@/lib/template-studio/draft/templateStudioDraft";
import { useTemplateLayout } from "@/lib/template-studio/repository/useTemplateLayout";
import { useScheduledBriefingsFullscreen } from "@/routes/scheduled-briefings/useScheduledBriefingsFullscreen";
import useAuth from "@/store/auth.slice";
import {
  loadDefinition,
  saveDefinition,
} from "@/store/scheduled-briefings/briefing-create/briefing-create.async-actions";
import useBriefingCreateStore, {
  selectHasUnsavedChanges,
  selectRecipientsError,
} from "@/store/scheduled-briefings/briefing-create/briefing-create.slice";

const getLoadErrorMessage = ({
  code,
}: BriefingDefinitionRepositoryError): string => {
  if (code === "NOT_FOUND") return "This briefing couldn't be found.";
  if (code === "NOT_OWNER") return "You don't have access to this briefing.";
  return "This briefing couldn't be loaded. Please try again.";
};

const getSaveErrorMessage = (
  error: BriefingDefinitionRepositoryError,
): string => {
  if (error.code === "NOT_OWNER") {
    return "You don't have permission to save this briefing.";
  }
  if (error.code === "STALE_DEFINITION") {
    return "This briefing was changed somewhere else. Reload to get the latest version; reloading discards your unsaved changes.";
  }
  if (error.code === "REQUEST_FAILURE") {
    return "The briefing couldn't be saved. Please try again.";
  }
  return error.message;
};

interface BriefingConfigureStepProps {
  templateId: string;
  isEditing: boolean;
  onClose: () => void;
}

/** Owns the template load so Save reads the same slots and version the view renders. */
const BriefingConfigureStep = ({
  templateId,
  isEditing,
  onClose,
}: BriefingConfigureStepProps) => {
  const templateLayout = useTemplateLayout(templateId);
  const { template } = templateLayout;
  const definitionId = useBriefingCreateStore((state) => state.definitionId);
  const saveError = useBriefingCreateStore((state) => state.definitionError);
  const isSaving = useBriefingCreateStore(
    (state) => state.isSavingBriefingDefinition,
  );
  const isReadOnly = useBriefingCreateStore(
    (state) => !state.canEditDefinition,
  );
  const isArchivedTemplate = !isEditing && template?.status === "archived";
  const hasRecipientsError = useBriefingCreateStore(
    (state) => selectRecipientsError(state) !== undefined,
  );
  const hasUnsavedChanges = useBriefingCreateStore(selectHasUnsavedChanges);
  // Set once a save succeeds, so the navigation that follows isn't blocked
  // before the clean state re-renders.
  const hasSavedRef = useRef(false);
  const leaveBlocker = useBlocker({
    shouldBlockFn: () => !hasSavedRef.current,
    disabled: !hasUnsavedChanges,
    withResolver: true,
  });

  const save = async () => {
    if (!template || isArchivedTemplate) return;

    const savedDefinitionId = await saveDefinition({
      slotIds: getSlotsInLayoutOrder(template.document).map((slot) => slot.id),
      templateUpdatedAt: template.updatedAt,
      fallbackBriefingName:
        getTemplateBriefingTitle(template.document) || DEFAULT_BRIEFING_NAME,
    });
    if (savedDefinitionId === null) return;

    hasSavedRef.current = true;
    toast({
      type: "success",
      title: isEditing ? "Briefing saved" : "Briefing created",
    });
    onClose();
  };

  return (
    <>
      <BriefingCreateWorkflowHeader
        step="configure"
        isEditing={isEditing}
        isReadOnly={isReadOnly}
        isSaving={isSaving}
        isSaveDisabled={!template || isArchivedTemplate || hasRecipientsError}
        onSave={isReadOnly ? undefined : () => void save()}
        onClose={onClose}
      />
      {isArchivedTemplate ? (
        <Alert
          variant="destructive"
          className="rounded-none border-x-0 border-t-0 border-red-100 bg-red-50 px-8 text-red-700"
        >
          <AlertDescription>
            This template is archived. Choose another template to create a
            briefing.
          </AlertDescription>
        </Alert>
      ) : null}
      {saveError ? (
        <Alert
          variant="destructive"
          className="items-center justify-between gap-4 rounded-none border-x-0 border-t-0 border-red-100 bg-red-50 px-8 text-red-700"
        >
          <AlertDescription>{getSaveErrorMessage(saveError)}</AlertDescription>
          {saveError.code === "STALE_DEFINITION" && definitionId !== null ? (
            <Button
              variant="outline"
              size="sm"
              onPress={() => void loadDefinition(definitionId)}
            >
              Reload
            </Button>
          ) : null}
        </Alert>
      ) : null}
      <ConfigureBriefingView
        templateLayout={templateLayout}
        isReadOnly={isReadOnly || isSaving}
      />
      <ScheduledBriefingsModals
        discardUnsavedChanges={{
          isOpen: leaveBlocker.status === "blocked",
          onLeave: () => leaveBlocker.proceed?.(),
          onStay: () => leaveBlocker.reset?.(),
        }}
      />
    </>
  );
};

/** Serves `/scheduled-briefings/create` and, with a `briefingId` param, edit of a saved briefing. */
const BriefingCreateRoute = () => {
  useScheduledBriefingsFullscreen();
  const navigate = useNavigate();
  const { briefingId } = useParams({ strict: false });
  const isEditing = briefingId !== undefined;
  const { templateId: searchTemplateId } = useSearch({ strict: false });
  // Set by the template preview's "Create briefing from this template".
  const preselectedTemplateId = isEditing ? undefined : searchTemplateId;
  const [searchTerm, setSearchTerm] = useState("");
  const step = useBriefingCreateStore((state) => state.step);
  const selectedTemplateId = useBriefingCreateStore(
    (state) => state.selectedTemplateId,
  );
  const definitionId = useBriefingCreateStore((state) => state.definitionId);
  const definitionError = useBriefingCreateStore(
    (state) => state.definitionError,
  );
  const selectTemplate = useBriefingCreateStore(
    (state) => state.selectTemplate,
  );
  const resetBriefingCreate = useBriefingCreateStore(
    (state) => state.resetBriefingCreate,
  );
  const seedOwnerRecipient = useBriefingCreateStore(
    (state) => state.seedOwnerRecipient,
  );
  // A ref so an auth update doesn't re-run the reset effect below.
  const ownerEmail = useAuth((state) => state.user.email);
  const ownerEmailRef = useRef(ownerEmail);
  useEffect(() => {
    ownerEmailRef.current = ownerEmail;
  }, [ownerEmail]);

  // The router keeps this instance between create and edit URLs, so reset on
  // every briefingId or preselected template change as well as on unmount.
  useEffect(() => {
    if (briefingId !== undefined) void loadDefinition(briefingId);
    else {
      // Edit keeps the saved recipients; only a new briefing starts with the owner.
      if (ownerEmailRef.current) seedOwnerRecipient(ownerEmailRef.current);
      if (preselectedTemplateId !== undefined) {
        selectTemplate(preselectedTemplateId);
      }
    }
    return () => {
      resetBriefingCreate();
    };
  }, [
    briefingId,
    preselectedTemplateId,
    resetBriefingCreate,
    seedOwnerRecipient,
    selectTemplate,
  ]);

  // Leaving an edit for /create renders once before the reset effect runs, so
  // treat leftover definition state as the chooser instead of configuring it.
  // A preselected template skips the chooser, including the render before the
  // effect copies it into the store.
  const createStep =
    definitionId !== null
      ? "choose-template"
      : preselectedTemplateId !== undefined
        ? "configure"
        : step;
  const configureTemplateId = selectedTemplateId ?? preselectedTemplateId;

  // The unmount effect resets the store, so a blocked close keeps the form.
  const closeWorkflow = () => {
    void navigate({ to: "/scheduled-briefings" });
  };

  const isConfiguring = isEditing
    ? definitionId === briefingId
    : createStep === "configure";

  const renderEditStatus = (editBriefingId: string) => {
    if (definitionError === null) {
      return (
        <div
          className="flex flex-1 items-center justify-center p-8"
          aria-busy="true"
          aria-live="polite"
        >
          <p className="text-sm text-gray-600">Loading briefing…</p>
        </div>
      );
    }

    const isRetryable =
      definitionError.code !== "NOT_FOUND" &&
      definitionError.code !== "NOT_OWNER";

    return (
      <div
        className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center"
        role="alert"
      >
        <p className="max-w-md text-sm text-gray-700">
          {getLoadErrorMessage(definitionError)}
        </p>
        <Button
          variant="outline"
          size="sm"
          onPress={
            isRetryable
              ? () => void loadDefinition(editBriefingId)
              : closeWorkflow
          }
        >
          {isRetryable ? "Try again" : "Back to briefings"}
        </Button>
      </div>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-gray-50">
      {isConfiguring && configureTemplateId ? (
        <BriefingConfigureStep
          templateId={configureTemplateId}
          isEditing={isEditing}
          onClose={closeWorkflow}
        />
      ) : (
        <>
          <BriefingCreateWorkflowHeader
            step={createStep}
            isEditing={isEditing}
            onClose={closeWorkflow}
          />
          {briefingId !== undefined ? (
            renderEditStatus(briefingId)
          ) : (
            <>
              <ChooseTemplateSectionHeader
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
              />
              <ChooseTemplateView
                searchTerm={searchTerm}
                onSelectTemplate={selectTemplate}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default BriefingCreateRoute;
